/**
 * Per-project task derivation for a store visit (#1955 / #1995).
 *
 * Identity rule (RMR / #1944): the ONLY reliable keys are
 *   - store  = legacyStoreId + retailerId  (legacyStoreId alone collides
 *              across retailers)
 *   - project = projectId
 * So every SiteTask carries all three ids. `projectName` is DISPLAY ONLY —
 * never used for matching.
 *
 * Source of truth is useVisitTask's MultiTaskVisitState (currentTask /
 * pausedTasks / completedTasks), which is already scoped to the active
 * visit and updates on every pause/finish — so the derived list reflects
 * real state and refreshes automatically. We do NOT read raw
 * visit_task_sessions (those records carry no storeId and use
 * projectDescription, which is what mis-wired the first cut).
 */

export type SiteTaskStatus = 'active' | 'paused' | 'completed' | 'todo';

export interface SiteTask {
  // Identity — the reliable key for the data models.
  legacyStoreId: number;
  retailerId: number | null;
  projectId: number;
  // Display only.
  projectName: string;
  status: SiteTaskStatus;
  callLogId?: number | null;
  taskUUID?: string;
}

/** The slice of useVisitTask's MultiTaskVisitState this derivation reads. */
export interface VisitTaskState {
  currentTask: { project_id: number; project_description: string } | null;
  currentTaskCallLogId: number | null;
  pausedTasks: Array<{ task: { project_id: number; project_description: string }; uuid: string }>;
  completedTasks: Array<{ project_id: number; project_description: string; calllog_id?: number }>;
}

export interface DeriveSiteTasksInput {
  legacyStoreId: number;
  retailerId: number | null;
  visitState: VisitTaskState;
  /** Projects scheduled/known at this store (id + display name). */
  scheduledProjects: Array<{ id: number; name: string }>;
}

const STATUS_ORDER: Record<SiteTaskStatus, number> = { active: 0, paused: 1, completed: 2, todo: 3 };

/**
 * Resolve a project's display name by id-match — NEVER positionally (#1996).
 *
 * A task is keyed by projectId; its name must follow the SAME projectId.
 * StoreDetail's synthetic-task builders used to pair
 * `project_id = projectIdFromNav` with `project_description =
 * projects[0].name`, so a second project worked at one store inherited a
 * DIFFERENT project's name (diag #37: #1068 shown as "Textured Hair", which
 * is #1102's name). That crossed pair was persisted to the IDB task record
 * and surfaced on the Today active card and the checkout summary.
 *
 * Match by id first; fall back to the nav description only when it belongs
 * to the same id; last resort is "#<id>".
 */
export function resolveProjectName(
  projectId: number,
  projects: Array<{ id: number; name: string }>,
  projectIdFromNav: number | null,
  projectDescFromNav: string | null,
): string {
  const byId = projects.find((p) => p.id === projectId);
  if (byId?.name) return byId.name;
  if (projectId === projectIdFromNav && projectDescFromNav) return projectDescFromNav;
  return `#${projectId}`;
}

export function deriveSiteTasks(input: DeriveSiteTasksInput): SiteTask[] {
  const { legacyStoreId, retailerId, visitState, scheduledProjects } = input;

  // Authoritative display name by projectId, sourced from the store's
  // scheduled / pending tasks. Prefer this over a task's STORED
  // project_description, which can be a bare "#<id>" fallback when a task was
  // started before its real name resolved (#2012 — caused "#1102 #1102" on the
  // active card while the schedule had "Textured Hair Service"). Still strictly
  // id-matched, never positional (#1996).
  const realName = (projectId: number, stored?: string | null): string => {
    const byId = scheduledProjects.find((p) => p.id === projectId);
    if (byId?.name && byId.name !== `#${projectId}`) return byId.name;
    if (stored && stored !== `#${projectId}`) return stored;
    return `#${projectId}`;
  };

  const byProject = new Map<number, SiteTask>();
  const consider = (t: SiteTask) => {
    const existing = byProject.get(t.projectId);
    // Higher-priority status wins (active > paused > completed > todo).
    if (!existing || STATUS_ORDER[t.status] < STATUS_ORDER[existing.status]) {
      byProject.set(t.projectId, t);
    }
  };
  const mk = (
    projectId: number,
    projectName: string,
    status: SiteTaskStatus,
    extra: Partial<SiteTask> = {},
  ): SiteTask => ({ legacyStoreId, retailerId, projectId, projectName, status, ...extra });

  // todo (schedule) first; live task state overrides it per precedence. Every
  // task's display name is resolved by id from the authoritative schedule so
  // the name is consistent (and never a bare "#<id>") across active/paused/todo.
  scheduledProjects.forEach((p) => consider(mk(p.id, realName(p.id, p.name), 'todo')));
  visitState.completedTasks.forEach((c) =>
    consider(mk(c.project_id, realName(c.project_id, c.project_description), 'completed', { callLogId: c.calllog_id ?? null })),
  );
  visitState.pausedTasks.forEach((p) =>
    consider(mk(p.task.project_id, realName(p.task.project_id, p.task.project_description), 'paused', { taskUUID: p.uuid })),
  );
  if (visitState.currentTask) {
    consider(
      mk(visitState.currentTask.project_id, realName(visitState.currentTask.project_id, visitState.currentTask.project_description), 'active', {
        callLogId: visitState.currentTaskCallLogId,
      }),
    );
  }

  return [...byProject.values()].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
}

// ---------------------------------------------------------------------------
// #2125 — ONE inclusion rule for "other startable tasks at this store".
//
// Diag #52 showed three surfaces disagreeing at the same instant: the Current
// Task banner said "+4 other tasks", the Other-tasks panel said 3, and the
// task picker listed 5 rows including a depleted "0 of 0 visits remaining"
// route. Each surface had its own ad-hoc filter. These helpers are now the
// single rule: exclude the given task ids (completed + current), and exclude
// DEPLETED calls — a route with 0 visits remaining is not startable and only
// confuses the count. Sets have no visit budget, so they only id-filter.
// ---------------------------------------------------------------------------

export function startableSets<T extends { task_id: number }>(sets: T[], excludeTaskIds: number[]): T[] {
  return sets.filter((t) => !excludeTaskIds.includes(t.task_id));
}

export function startableCalls<T extends { task_id: number; visits_remaining?: number | null }>(
  calls: T[],
  excludeTaskIds: number[],
): T[] {
  return calls.filter((t) => !excludeTaskIds.includes(t.task_id) && t.visits_remaining !== 0);
}
